import { useRef } from "react";
import type { DateRangeWindow } from "@acto/genui-components-schema";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { KpiComparisonMode, ProductOrderBy, ProductRowScope } from "@acto/gql/graphql";
import type { Kpi } from "@acto/taskpane/core/domain";
import { ProductTrendList } from "@acto/taskpane/field-sales-2/components/primitives/ProductTrendList";
import { formatMonthYearRange } from "@acto/taskpane/field-sales-2/utils/format";
import { kpiTabLabelKey } from "@acto/taskpane/field-sales-2/data-access/buildKpiTrend";
import { useProductGroups } from "@acto/taskpane/field-sales-2/data-access/useProductGroups";
import { useInfiniteScroll } from "@acto/taskpane/field-sales-2/pages/useInfiniteScroll";
import { CardHeadline } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import {
  type DrillLevel,
  DrillOverlay,
  Sentinel,
  useDrillStack,
} from "@acto/taskpane/field-sales-2/components/patterns/drilldown";

/**
 * Scope for one drill, resolved from the streamed card at the moment a row is opened. None of it is
 * recoverable from the URL, so a reload lands back on the story feed.
 */
export interface ProductDrillTarget {
  clientId: string;
  eyebrow: string;
  label: string;
  parentGroupIds: string[];
  kpi: Kpi;
  dateWindow: DateRangeWindow;
  order: ProductOrderBy;
}

// A page has to overflow the scrollport, or the sentinel is on screen when the level opens and the
// list pages again before the user scrolls.
const DRILL_PAGE_SIZE = 10;

interface ProductDrillLevel extends Pick<DrillLevel, "name"> {
  parents: string[];
}

interface ProductDrillOverlayProps {
  target: ProductDrillTarget;
  onClose: () => void;
}

export function ProductDrillOverlay({ target, onClose }: ProductDrillOverlayProps) {
  const { locale } = useLocale();
  const t = useTranslations("fieldSalesTwo.productDrill");
  const tKpi = useTranslations("kpi");
  const { clientId, kpi, dateWindow, eyebrow, order } = target;
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { stack, current, push, drillTo } = useDrillStack<ProductDrillLevel>(
    { name: target.label, parents: target.parentGroupIds },
    onClose,
  );

  const childGroups = useProductGroups({
    clientId,
    parents: current.parents,
    dateRange: { startDate: dateWindow.start, endDate: dateWindow.end },
    selectedKpi: kpi,
    // Meeting prep measures its deltas period over period and the streamed card does not declare
    // the mode, so the drill pins it. Year over year would measure each level against a different
    // window than the row the user opened.
    comparisonMode: KpiComparisonMode.PeriodOverPeriod,
    // This drill explains a delta, so it lists products active in either compared window.
    rowScope: ProductRowScope.EitherPeriod,
    order,
    pageSize: DRILL_PAGE_SIZE,
  });

  const sentinelRef = useInfiniteScroll<HTMLDivElement>({
    hasMore: childGroups.hasMore,
    isLoadingMore: childGroups.isLoadingMore,
    loadMore: childGroups.loadMore,
    resetKey: childGroups.items.length,
    root: scrollAreaRef,
  });

  return (
    <DrillOverlay
      eyebrow={eyebrow}
      headline={<CardHeadline>{current.name}</CardHeadline>}
      stack={stack}
      onDrillTo={drillTo}
      scrollAreaRef={scrollAreaRef}
    >
      <ProductTrendList
        captionScrollRef={scrollAreaRef}
        nested
        items={childGroups.items}
        caption={t("drillMeta", { kpi: tKpi(kpiTabLabelKey(kpi)), range: formatMonthYearRange(dateWindow, locale) })}
        isLoading={childGroups.isPending}
        emptyLabel={t("drillEmpty")}
        onItemClick={(group) => push({ name: group.name, parents: [group.id] })}
        footer={childGroups.hasMore ? <Sentinel ref={sentinelRef} aria-hidden="true" /> : undefined}
      />
    </DrillOverlay>
  );
}
